import { components } from '../../models/dms-api';

/**
 * Per-Entity-Linktabellen-Typ — Backend-Enum-Werte (Singular, deutsch).
 * Single-Source-of-Truth aus OpenAPI-Codegen.
 *
 * Werte: 'vertrag' | 'projekt' | 'leistungsbild' | 'leistungsphase'
 *      | 'teilleistung' | 'organisation' | 'ansprechpartner'.
 *
 * Spec-Coverage: W10-Spec §6, Plan V5.
 */
export type DmsLinkEntityType = components['schemas']['DocumentLinkEntityType'];

/**
 * URL-Pfad-Segment für Sub-Controller-Endpoints (`/api/v1/${segment}/${entityId}/...`).
 * Plural, deutsch-mixed — passt zu den Symfony-Route-Definitions.
 *
 * NICHT zu verwechseln mit DmsLinkEntityType (Backend-Enum-Werte).
 *
 * **Re-Export, keine zweite Deklaration** (2026-08-07): Hier stand bis zum
 * Welle-3-Gesamtsmoke eine wortgleiche Kopie der Union aus
 * `services/dms-document-link.service.ts`. Beide trugen zufaellig denselben Stand,
 * aber keine Konsumentenstelle importierte diese Kopie — sie war eine reine
 * Drift-Quelle derselben Art, die im Verknuepfungen-Tab tatsaechlich zugeschlagen hat.
 * Die Union wird jetzt genau einmal deklariert (im Service, wo die Segmente die
 * Route bilden) und hier nur weitergereicht.
 */
export type { DmsLinkEntityUrlSegment } from '../../services/dms-document-link.service';
import type { DmsLinkEntityUrlSegment as DmsLinkEntityUrlSegmentIntern } from '../../services/dms-document-link.service';

/**
 * Mapping Backend-Enum-Wert → URL-Pfad-Segment.
 *
 * **Diese Map ist die einzige vollstaendige Aufzaehlung der Linktypen im Frontend** und
 * wird deshalb ueber `Record<DmsLinkEntityType, …>` vom Compiler erzwungen. Der
 * Verknuepfungen-Tab leitet seine Sektionsliste per `Object.values()` hieraus ab
 * (Fix 2026-08-07) — Schluesselreihenfolge = Anzeigereihenfolge.
 *
 * **Beim Hinzufuegen eines Linktyps ausserdem zu pflegen** (keine dieser Stellen wird
 * vom Compiler erzwungen, deshalb hier die Liste):
 *  - `ENTITY_TYPE_LABEL` unten (ist `Record`-getypt, faellt also auf)
 *  - `ENTITY_LABEL` + `URL_SEGMENT_TO_ENTITY_TYPE` in
 *    `views/dms/components/dms-document-links-section/…component.ts` (beide `Record`-getypt)
 *  - `switch` in `shared/entity-lookup/entity-link-router.ts` (hat `default`, faellt NICHT auf)
 *  - `entityTypeLabel()` in `views/portal/shared/audit-timeline/audit-historie-presentation.ts`
 *    (hat `default`, faellt NICHT auf)
 *  - `NUR_LESEN_SEGMENTE` im Verknuepfungen-Tab, falls der Typ keinen Subcontroller hat
 *  - backendseitig: die fuenf Enum-Kopien in `nelmio_api_doc.yaml`, gewacht von
 *    `DmsApiDocSmokeTest::testJedeEntityTypEnumListeImSpecIstVollstaendig()`
 */
export const ENTITY_TYPE_TO_URL_SEGMENT: Record<DmsLinkEntityType, DmsLinkEntityUrlSegmentIntern> = {
  projekt: 'projects',
  vertrag: 'vertraege',
  leistungsbild: 'leistungsbilder',
  leistungsphase: 'leistungsphasen',
  teilleistung: 'teilleistungen',
  organisation: 'organisationen',
  ansprechpartner: 'ansprechpartner',
  bestandsknoten: 'bestandsknoten',
  stundennachweis: 'stundennachweise',
};

/**
 * Mapping Backend-Enum-Wert → menschenlesbares Label (deutsch).
 * Verwendet in DmsUploadModalComponent (Lock-UI) und ggf. weiteren Stellen.
 */
export const ENTITY_TYPE_LABEL: Record<DmsLinkEntityType, string> = {
  projekt: 'Projekt',
  vertrag: 'Vertrag',
  leistungsbild: 'Leistungsbild',
  leistungsphase: 'Leistungsphase',
  teilleistung: 'Teilleistung',
  organisation: 'Organisation',
  ansprechpartner: 'Ansprechpartner',
  bestandsknoten: 'Bestandsknoten',
  stundennachweis: 'Stundennachweis',
};

/**
 * Alle Linktypen in Anzeigereihenfolge — **abgeleitet**, nicht handgepflegt.
 *
 * Quelle ist `ENTITY_TYPE_TO_URL_SEGMENT`: die einzige Aufzaehlung im Frontend, deren
 * Vollstaendigkeit der Compiler ueber `Record<DmsLinkEntityType, …>` erzwingt. Ein neuer
 * Backend-Enum-Wert bricht dort den Build; jede Liste, die hier ableitet, wandert
 * automatisch mit.
 *
 * Hintergrund (Whole-Branch-Review I1): vier DMS-Admin-Masken fuehrten dieselbe Werteliste
 * von Hand und waren auf SIEBEN Werten stehen geblieben — es fehlten `bestandsknoten` und
 * `stundennachweis`. Sichtbare Folge: das Bearbeiten-Modal des geseedeten System-
 * Verknuepfungstyps `abschluss_beleg` rendert sein Entity-Typ-Auswahlfeld leer
 * (`selectedIndex = -1`, der native-select-Fallstrick dieses Projekts) und ist damit
 * nicht von „gilt fuer alle" zu unterscheiden — wer das Feld anfasst, bindet einen
 * System-Typ still um. Ausserdem liess sich fuer die beiden fehlenden Typen kein
 * Verknuepfungstyp anlegen, obwohl das Backend genau dafuer erweitert wurde.
 *
 * Gleiches Muster wie im Verknuepfungen-Tab (Task 17), dort ueber `Object.values(...)`.
 */
export const ALLE_ENTITY_TYPES = Object.keys(ENTITY_TYPE_TO_URL_SEGMENT) as DmsLinkEntityType[];

/**
 * Linktypen, deren Verknuepfungen ausschliesslich systemseitig entstehen — Spiegel von
 * `DocumentLinkEntityType::istSystemseitigVerknuepft()` im Backend.
 *
 * Fuer sie gilt: kein Nutzer-Upload, kein Entity-Picker (der Lookup wirft bewusst), und
 * die generische Link-API weist sie mit 422 `link_typ_systemseitig` ab
 * (Whole-Branch-Review C1). Ein Administrator kann also durchaus einen Verknuepfungstyp
 * daran binden — nur benutzen kann ihn niemand. Genau das macht der Marker in
 * {@link entityTypeOptionen} sichtbar.
 *
 * **Warum eine Hand-Liste, obwohl I1 genau die abgeschafft hat:** Sie behauptet keine
 * VOLLSTAENDIGKEIT, sondern markiert eine Teilmenge. Ein fehlender Eintrag laesst hier
 * einen Hinweis weg (kosmetisch), waehrend ein fehlender Eintrag in einer
 * Vollstaendigkeits-Liste einen Typ unsichtbar macht (Defekt). Dasselbe Muster wie
 * `NUR_LESEN_SEGMENTE` im Verknuepfungen-Tab.
 */
export const SYSTEMSEITIGE_ENTITY_TYPES: readonly DmsLinkEntityType[] = ['stundennachweis'];

/** Marker-Suffix fuer systemseitig vergebene Linktypen in Auswahlfeldern und Tabs. */
export const SYSTEMSEITIG_MARKER = ' (systemseitig vergeben)';

/**
 * Options-/Tab-Liste ueber ALLE Linktypen, in der Reihenfolge von {@link ALLE_ENTITY_TYPES}.
 *
 * `labelUeberschreibungen` deckt die Faelle ab, in denen eine Maske bewusst anders
 * beschriftet als `ENTITY_TYPE_LABEL` (z.B. „Natürliche Person" statt „Ansprechpartner"
 * in den Vorbelegungs-Masken). Die Ueberschreibung aendert nur den Text, nie die
 * Vollstaendigkeit — genau das war der Fehler der handgepflegten Listen.
 *
 * Systemseitige Typen ({@link SYSTEMSEITIGE_ENTITY_TYPES}) bekommen
 * {@link SYSTEMSEITIG_MARKER} angehaengt. Sie BLEIBEN in der Liste, weil die Migration
 * einen System-Verknuepfungstyp mit `entityType: 'stundennachweis'` seedet: nimmt man den
 * Wert heraus, rendert dessen Bearbeiten-Dialog das Auswahlfeld wieder leer — also genau
 * der Defekt, den I1 behoben hat. Der Marker ist deshalb der ehrlichere Weg als ein
 * Ausblenden: er verhindert nicht, dass jemand einen unbenutzbaren Typ anlegt, aber er
 * sagt es ihm vorher.
 *
 * Bewusst NICHT in `ENTITY_TYPE_LABEL` eingebaut: dort wuerde der Marker in Listen,
 * Badges und dem Verknuepfungen-Tab auftauchen, wo er nichts erklaert. Nur die vier
 * Admin-Masken rufen diese Funktion.
 */
export function entityTypeOptionen(
  labelUeberschreibungen: Partial<Record<DmsLinkEntityType, string>> = {},
): { type: DmsLinkEntityType; label: string; systemseitig: boolean }[] {
  return ALLE_ENTITY_TYPES.map((type) => {
    const systemseitig = SYSTEMSEITIGE_ENTITY_TYPES.includes(type);

    return {
      type,
      systemseitig,
      label: (labelUeberschreibungen[type] ?? ENTITY_TYPE_LABEL[type]) + (systemseitig ? SYSTEMSEITIG_MARKER : ''),
    };
  });
}

/**
 * Re-Export der kanonischen Single-Source (shared/entity-lookup/entity-tree-picker.types).
 * Konkrete Typnamen wo bekannt, generischer Fallback „Eintrag" (nie „Entität") — Spec §4 / D8.
 * Cross-Phase Consistency Rule 3: Phase B/C definieren keine eigene Implementierung.
 */
export { entityTypeLabel } from '../../shared/entity-lookup/entity-tree-picker.types';
